import { fetchOncallDuty, type DutyEntry } from "@/api/duty";
import { ackIncident, fetchIncidents, type IncidentResponse, resolveIncident } from "@/api/incident";
import { type MemberNameMapResponse } from "@/api/member";
import { useTeamStore } from "@/app/store/useTeamStore";
import { NoTeamSelected } from "@/shared/components/NoTeamSelected";
import { Button } from "@/shared/components/ui/button";
import { toast } from "@/shared/hooks/useToast";
import { useMemberNameMap } from "@/shared/hooks/useMemberNameMap";
import { Card, CardContent } from "@/shared/components/ui/card";
import { cn } from "@/shared/libs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

// ── 멤버별 파스텔 배경색 팔레트 ──────────────────────────────
const MEMBER_COLORS = [
  "bg-yellow-100 text-yellow-800",
  "bg-purple-100 text-purple-800",
  "bg-pink-100   text-pink-800",
  "bg-blue-100   text-blue-800",
  "bg-green-100  text-green-800",
  "bg-orange-100 text-orange-800",
  "bg-teal-100   text-teal-800",
  "bg-red-100    text-red-800",
];

function memberColor(memberId: number, allMemberIds: number[]) {
  const idx = allMemberIds.indexOf(memberId);
  return MEMBER_COLORS[idx % MEMBER_COLORS.length] ?? MEMBER_COLORS[0];
}

function getMemberName(id: number | null, members: MemberNameMapResponse[]) {
  if (id == null) return "-";
  return members.find((m) => m.id === id)?.name ?? "알 수 없음";
}

// ── 날짜 유틸 ────────────────────────────────────────────────
function toDateStr(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isSameDay(a: Date, b: Date) {
  return toDateStr(a) === toDateStr(b);
}

function buildCalendarGrid(year: number, month: number) {
  // month: 0-indexed
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // 일요일 시작 (0=Sun … 6=Sat)
  const cells: (Date | null)[] = Array(firstDay.getDay()).fill(null);

  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push(new Date(year, month, d));
  }
  // 마지막 주 채우기
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// ── 활성 인시던트 배너 ────────────────────────────────────────
interface IncidentBannerProps {
  incident: IncidentResponse;
  members: MemberNameMapResponse[];
  teamId: number;
}

function IncidentBanner({ incident, members, teamId }: IncidentBannerProps) {
  const queryClient = useQueryClient();

  const ackMut = useMutation({
    mutationFn: () => ackIncident(teamId, incident.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["incidents", teamId] }),
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Ack 처리 실패",
        description: error.message || "인시던트 확인(Ack) 중 오류가 발생했습니다.",
      });
    },
  });
  const resolveMut = useMutation({
    mutationFn: () => resolveIncident(teamId, incident.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["incidents", teamId] }),
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Resolve 처리 실패",
        description: error.message || "인시던트 해결(Resolve) 중 오류가 발생했습니다.",
      });
    },
  });

  const ackedBy = incident.ack_resolve_history.find((h) => h.status === "acked");

  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
      <div className="flex min-w-0 gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <div className="space-y-0.5">
          <p className="flex items-center gap-2 font-semibold text-destructive">
            현재 온콜 상황 발생
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-destructive" />
          </p>
          <p className="font-medium">{incident.title}</p>
          <p className="text-sm text-muted-foreground">
            발생 시각:{" "}
            {incident.created_at
              ? new Date(incident.created_at).toLocaleString("ko-KR")
              : "-"}
          </p>
          {ackedBy && (
            <p className="text-sm text-muted-foreground">
              담당자: {getMemberName(ackedBy.member_id, members)}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 gap-2">
        {incident.status === "open" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => ackMut.mutate()}
            disabled={ackMut.isPending}
          >
            확인
          </Button>
        )}
        {(incident.status === "open" || incident.status === "acked") && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => resolveMut.mutate()}
            disabled={resolveMut.isPending}
          >
            해결
          </Button>
        )}
      </div>
    </div>
  );
}

// ── 월간 캘린더 ───────────────────────────────────────────────
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

interface DutyCalendarProps {
  year: number;
  month: number;
  entries: DutyEntry[];
  members: MemberNameMapResponse[];
  allMemberIds: number[];
  onPrev: () => void;
  onNext: () => void;
}

function DutyCalendar({
  year, month, entries, members, allMemberIds, onPrev, onNext,
}: DutyCalendarProps) {
  const today = new Date();
  const cells = buildCalendarGrid(year, month);

  const entryMap = new Map<string, DutyEntry>();
  entries.forEach((e) => {
    entryMap.set(toDateStr(new Date(e.date)), e);
  });

  const monthLabel = new Date(year, month).toLocaleString("ko-KR", {
    month: "long",
    year: "numeric",
  });

  return (
    <Card>
      <CardContent className="p-4">
        {/* 헤더 */}
        <div className="mb-4 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={onPrev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-semibold">{monthLabel}</span>
          <Button variant="ghost" size="icon" onClick={onNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 mb-1">
          {WEEKDAYS.map((d) => (
            <div key={d} className="py-1 text-center text-xs font-medium text-muted-foreground">
              {d}
            </div>
          ))}
        </div>

        {/* 날짜 셀 */}
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((date, idx) => {
            if (!date) {
              return <div key={`empty-${idx}`} className="aspect-[1/0.65] rounded" />;
            }

            const dateStr = toDateStr(date);
            const entry = entryMap.get(dateStr);
            const isToday = isSameDay(date, today);
            const isPast = !isToday && date < today;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const memberId = entry?.member_ids[0];
            const isOverride = entry?.is_override ?? false;

            // 로테이션 첫날 여부: 전날 entry가 다른 멤버이거나 override였거나 없을 때
            const prevDate = new Date(date);
            prevDate.setDate(prevDate.getDate() - 1);
            const prevEntry = entryMap.get(toDateStr(prevDate));
            const isRotationStart =
              !isOverride &&
              (!prevEntry ||
                prevEntry.is_override ||
                prevEntry.member_ids[0] !== memberId);

            // 이름 표기 여부
            const showName = memberId != null && (isOverride || isRotationStart);

            const colorClass = memberId != null
              ? memberColor(memberId, allMemberIds)
              : isWeekend
              ? "bg-muted/40"
              : "";

            return (
              <div
                key={dateStr}
                aria-disabled={isPast || undefined}
                aria-label={isPast ? "이전 날짜는 수정할 수 없습니다" : undefined}
                title={isPast ? "이전 날짜는 수정할 수 없습니다" : undefined}
                className={cn(
                  "aspect-[1/0.65] rounded p-1 text-xs",
                  colorClass,
                  isToday && "ring-2 ring-foreground ring-offset-1",
                  isPast && "opacity-50 cursor-not-allowed",
                )}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full font-medium",
                    isToday && "bg-foreground text-background",
                  )}
                >
                  {date.getDate()}
                </span>
                {showName && (
                  <p
                    className={cn(
                      "mt-0.5 truncate font-medium leading-tight",
                      isOverride && "text-red-600 dark:text-red-400",
                    )}
                  >
                    {getMemberName(memberId!, members)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────
export function OncallOverviewPage() {
  const { selectedTeam } = useTeamStore();
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  const monthStart = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-01T00:00:00`;
  const nextMonthStart = calMonth === 11
    ? `${calYear + 1}-01-01T00:00:00`
    : `${calYear}-${String(calMonth + 2).padStart(2, "0")}-01T00:00:00`;

  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayMonthStart = `${todayYear}-${String(todayMonth + 1).padStart(2, "0")}-01T00:00:00`;
  const todayNextMonthStart = todayMonth === 11
    ? `${todayYear + 1}-01-01T00:00:00`
    : `${todayYear}-${String(todayMonth + 2).padStart(2, "0")}-01T00:00:00`;

  const { data: duty } = useQuery({
    queryKey: ["duty", selectedTeam?.id, calYear, calMonth],
    queryFn: () => fetchOncallDuty(selectedTeam!.id, monthStart, nextMonthStart),
    enabled: !!selectedTeam,
    refetchOnMount: "always",
  });

  // 오늘 담당자는 캘린더 표시 월과 독립적으로 조회
  const { data: todayDuty } = useQuery({
    queryKey: ["duty-today", selectedTeam?.id],
    queryFn: () => fetchOncallDuty(selectedTeam!.id, todayMonthStart, todayNextMonthStart),
    enabled: !!selectedTeam,
    refetchOnMount: "always",
  });

  const { data: incidents = [] } = useQuery({
    queryKey: ["incidents", selectedTeam?.id],
    queryFn: () => fetchIncidents(selectedTeam!.id),
    enabled: !!selectedTeam,
    refetchInterval: 10000,
  });

  const { nameMap } = useMemberNameMap();
  const members = useMemo(() => Array.from(nameMap.values()), [nameMap]);

  if (!selectedTeam) return <NoTeamSelected />;

  const activeIncidents = incidents.filter(
    (i) => i.status === "open" || i.status === "acked"
  );

  const todayStr = toDateStr(today);
  const todayEntry = todayDuty?.entries.find((e) => toDateStr(new Date(e.date)) === todayStr);
  const todayOncallId = todayEntry?.member_ids[0];
  const todayOncallName = todayOncallId != null
    ? getMemberName(todayOncallId, members)
    : null;

  // 캘린더용 전체 멤버 ID 목록 (색상 인덱스 일관성)
  const allMemberIds = members.map((m) => m.id);

  const handlePrev = () => {
    if (calMonth === 0) { setCalYear((y) => y - 1); setCalMonth(11); }
    else setCalMonth((m) => m - 1);
  };
  const handleNext = () => {
    if (calMonth === 11) { setCalYear((y) => y + 1); setCalMonth(0); }
    else setCalMonth((m) => m + 1);
  };

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-bold">대시보드</h1>
        <p className="text-sm text-muted-foreground">{selectedTeam.name}</p>
      </div>

      {/* 활성 인시던트 배너 */}
      {activeIncidents.map((incident) => (
        <IncidentBanner
          key={incident.id}
          incident={incident}
          members={members}
          teamId={selectedTeam.id}
        />
      ))}

      {/* 오늘 온콜 담당자 */}
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          {todayOncallName ? (
            <p className="text-2xl font-bold">
              현재 온콜 담당자는{" "}
              <span className="text-primary">{todayOncallName}</span> 님 입니다
            </p>
          ) : (
            <p className="text-lg text-muted-foreground">오늘 온콜 담당자가 없습니다</p>
          )}
        </CardContent>
      </Card>

      {/* 월간 듀티 캘린더 */}
      <DutyCalendar
        year={calYear}
        month={calMonth}
        entries={duty?.entries ?? []}
        members={members}
        allMemberIds={allMemberIds}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </div>
  );
}
